'use client';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../lib/i18n';
import { ErrorState, Spinner } from './states';

type Read = { src: string; url: string } | { src: string; failed: true };

/** The one in-app PDF view: Editor's PDF and converted Office files, Mail's PDF attachments. It fills the
 *  bounded parent the caller gives it and leaves the drawing to the browser's own PDF viewer in an
 *  `<iframe>`.
 *
 *  `src` is a same-origin browser URL (`/api/...`), read with the cookie session like an `<img src>`, so no
 *  bearer token enters a URL. The frame never loads `src` itself: a download route answers with
 *  `Content-Disposition: attachment`, which a frame would save instead of showing. The bytes are fetched
 *  and re-wrapped as `application/pdf` WHATEVER the response declared, because a blob URL belongs to the
 *  app's origin and an HTML or SVG blob framed here would run as the app. A PDF viewer is the only thing
 *  this frame can ever start.
 *
 *  The object URL belongs to one `src`: a new `src` or an unmount aborts the read in flight and revokes
 *  the URL, and a read that settles for a source no longer shown publishes nothing. While the bytes are in
 *  flight the box shows a spinner; a refused or failed read states that the preview could not be loaded.
 *  Size admission (how large a file may be buffered) and any phone-specific presentation stay with the
 *  caller. */
export function PdfPreview({ src, title }: { src: string; title: string }) {
  const { t } = useTranslation();
  const [read, setRead] = useState<Read | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    fetch(src, { credentials: 'same-origin', signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error(`PDF preview read answered ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setRead({ src, url: objectUrl });
      })
      .catch(() => { if (!controller.signal.aborted) setRead({ src, failed: true }); });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const current = read?.src === src ? read : null;
  return (
    <div className="relative h-full min-h-0 w-full">
      {current === null ? (
        <div className="flex h-full items-center justify-center">
          <Spinner size="lg" label={t.common.loading} />
        </div>
      ) : 'failed' in current ? (
        <div className="flex h-full items-center justify-center">
          <ErrorState message={t.common.documentPreviewFailed} />
        </div>
      ) : (
        <iframe src={current.url} title={title} className="block h-full w-full rounded-md border border-border bg-background" />
      )}
    </div>
  );
}
